import React, { ChangeEvent, useState } from 'react';
import { styled } from 'styled-components';
import { DazzleAddress } from '@dazzle/api-client';

import Modal from 'Common/components/Modal';
import { useModalContext } from 'Common/contexts/ModalContext';
import { FontFamily, Radius, Spacing } from 'Common/constants/theme';
import { Button, ButtonTheme } from '../styled/Button';
import DeleteIcon from '../icons/DeleteIcon';

const EditAddressesModal = ({ inferredAddresses, addresses, onSave }: {
  inferredAddresses: DazzleAddress[];
  addresses: DazzleAddress[];
  onSave: (addresses: DazzleAddress[]) => void;
}) => {
  const modalContext = useModalContext();
  const [myAddresses, setMyAddresses] = useState(addresses);

  const onAddressChange = (index: number, e: ChangeEvent<HTMLTextAreaElement>) => {
    const newMyAddresses = [...myAddresses];
    newMyAddresses[index] = {
      address: e.currentTarget.value
    };
    setMyAddresses(newMyAddresses);
  };

  const onDelete = (index: number) => {
    const newMyAddresses = [...myAddresses];
    newMyAddresses.splice(index, 1)
    setMyAddresses((
      newMyAddresses
    ));
  };

  const addNewAddress = () => {
    setMyAddresses([...myAddresses, { address: '' }]);
  };

  return <Modal onClickOutside={modalContext.hide} maxWidth="800px">
    <ModalContainer data-testid="edit-address-modal">
      <ModalTitle>Edit Addresses</ModalTitle>

      <ScrollableContent>
        {inferredAddresses.length > 0 && <Section>
          <SectionHeading>Inferred Addresses</SectionHeading>
          <AddressList>
            {
              inferredAddresses.map((address, i) => <AddressItem key={i}>
                {address.address}
              </AddressItem>)
            }
          </AddressList>
        </Section>}

        <Section>
          <SectionHeading>User Addresses</SectionHeading>
          <AddressList>
            {
              myAddresses.map((address, i) => <AddressRow key={i}>
                <StyledTextArea
                  rows={2}
                  value={address.address}
                  onChange={(e) => onAddressChange(i, e)}
                  placeholder="Enter an address..."
                />
                <DeleteContainer onClick={() => onDelete(i)} title="delete">
                  <DeleteIcon/>
                </DeleteContainer>
              </AddressRow>)
            }
          </AddressList>
          <Button onClick={addNewAddress} $theme={ButtonTheme.secondary} style={{ marginTop: Spacing.small }}>
            Add new address
          </Button>
        </Section>
      </ScrollableContent>

      <ButtonGroup>
        <Button onClick={modalContext.hide} $theme={ButtonTheme.secondary}>Cancel</Button>
        <Button onClick={() => onSave(myAddresses)}>Save</Button>
      </ButtonGroup>
    </ModalContainer>
  </Modal>;
};

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  min-width: 500px;
  font-family: ${FontFamily};
`;

const ModalTitle = styled.h2`
  margin: 0 0 ${Spacing.medium}px 0;
  font-size: 20px;
  color: #333;
`;

const ScrollableContent = styled.div`
  overflow-y: auto;
  padding-right: ${Spacing.small}px;
  margin-bottom: ${Spacing.large}px;
`;

const Section = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: ${Radius.medium}px;
  padding: ${Spacing.medium}px;
  margin-bottom: ${Spacing.medium}px;
  background-color: #f9fafb;
`;

const SectionHeading = styled.h3`
  margin: 0 0 ${Spacing.small}px 0;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.small}px;
`;

const AddressItem = styled.div`
  padding: ${Spacing.small}px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: ${Radius.small}px;
  font-size: 14px;
  color: #374151;
`;

const AddressRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: ${Spacing.small}px;
  border: 1px solid #d1d5db;
  border-radius: ${Radius.small}px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  min-height: 60px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Spacing.medium}px;
  padding-top: ${Spacing.medium}px;
  border-top: 1px solid #e5e7eb;
`;

const DeleteContainer = styled.div`
  cursor: pointer;
`;

export default EditAddressesModal;
